/* ---------------------------------------------------------------------
 *                           Import Library
 * --------------------------------------------------------------------- */

// utils
import { get, post } from "./api";
import { isLoggedIn, setLoginData } from "../data/userData"
import { clearSession, clearStorage } from "../data/localCache";
import { LOGIN_PATH, REGISTER_PATH, RESET_PASSWORD_PATH, GRANT_SMS, GRANT_PASSWORD, CLIENT_ID, TENANT_ID, SMS_PATH } from "../utils/constants";

/* ---------------------------------------------------------------------
 *                               SMS API
 * --------------------------------------------------------------------- */

/**
 * 获取短信验证码
 * @param {object} data - 表单数据
 * @param {string} data.mobile - 手机号 
 * @returns {Promise<{success: boolean, message: string, data: object}>}
 */
export async function sendSmsCode(data) {
    try {
        await get(SMS_PATH, data);
        return {
            success: true,
            message: '获取验证码成功',
        }
    } catch (error) {
        // 返回后端错误信息或默认错误
        const message = error?.response?.data?.message || error.message || "获取验证码失败";
        return { success: false, message };
    }
}

/* ---------------------------------------------------------------------
 *                              Login API
 * --------------------------------------------------------------------- */

/**
 * 初始化用户缓存数据
 * 
 * 该函数接收用户认证数据并将其存储在本地缓存中，
 * 用于维护用户会话状态。它会创建一个结构化的登录数据对象，
 * 包含认证令牌和相关信息。
 * 
 * @param {object} userData - 用户认证数据对象，包含以下属性：
 *   @property {string} accessToken - API认证用的访问令牌
 *   @property {number} expireIn - 访问令牌的过期时间（秒）
 *   @property {string} refreshToken - 用于刷新访问令牌的刷新令牌
 *   @property {number} refreshExpireIn - 刷新令牌的过期时间（秒）
 *   @property {string} clientId - 应用程序的客户端标识符
 *   @property {string} scope - 令牌的授权范围
 * 
 * @returns {Promise<void>} - 当缓存初始化完成时解析的Promise
 */
async function initUserCache(userData) {
    if (userData) {
        try {
            const loginData = {
                loggedIn: true,
                accessToken: userData,
            }
            setLoginData(loginData);
        } catch (error) {
            throw new Error('初始化失败: ' + error.message);
        }
    }
}

/**
 * 登录用户
 * @param {string} type - 登录类型 
 * @param {object} userObj - 登录对象
 * @param {string} userObj.mobile - 手机号
 * @param {string} userObj.smsCode - 验证码
 * @param {string} userObj.password - 密码
 * @returns {string} 登录成功返回空字符串，否则返回错误信息
 */
export async function loginUser(type, userObj) {
    try {
        if (isLoggedIn()) {
            return { success: true, message: "用户已登录" };
        }


        // 封装请求数据
        const loginRequest = {
            mobile: userObj?.mobile,
            smsCode: userObj.smsCode || "",
            password: userObj.password || "",
            grantType: type === "password" ? GRANT_PASSWORD : GRANT_SMS,
            clientId: CLIENT_ID,
            tenantId: TENANT_ID,
        }

        const response = await post(LOGIN_PATH, loginRequest);

        if (response.data) {
            await initUserCache(response.data);
            return { success: true, message: "登录成功", };
        } else {
            return { success: false, message: "登录失败" };
        }
    } catch (error) {
        // 返回后端错误信息或默认错误
        const message = error?.response?.data?.message || error.message || "登录失败";
        return { success: false, message };
    }
}

/* ---------------------------------------------------------------------
 *                              Logout API
 * --------------------------------------------------------------------- */


/**
 * 注销用户
 * @returns {Promise<{success: boolean, message: string}>} 注销结果
 */
export async function logoutUser() {
    try {
        clearSession();
        clearStorage();
        return { success: true, message: "注销成功" };
    } catch (error) {
        return {
            success: false,
            message: error?.response?.data?.message || "注销失败"
        };
    }
}

/* ---------------------------------------------------------------------
 *                              Register API
 * --------------------------------------------------------------------- */

/**
 * 注册用户
 * @param {object} userObj - 注册对象
 * @param {string} userObj.mobile - 手机号
 * @param {string} userObj.smsCode - 验证码
 * @param {string} userObj.password - 密码
 * @returns {string} 注册成功返回空字符串，否则返回错误信息
 */
export async function registerUser(userObj) {
    try {
        await post(REGISTER_PATH, userObj)
        return { success: true, message: "注册成功" };
    } catch (error) {
        // 返回后端错误信息或默认错误
        const message = error?.response?.data?.message || error.message || "注册失败";
        return { success: false, message };
    }
}

/* ---------------------------------------------------------------------
 *                             ResetPassword API
 * --------------------------------------------------------------------- */

/**
 * 重设密码
 * @param {object} userObj - 重设密码对象
 * @param {string} userObj.mobile - 手机号
 * @param {string} userObj.smsCode - 验证码
 * @param {string} userObj.newPwd - 新密码
 * @returns {string} 重设密码成功返回空字符串，否则返回错误信息
 */
export async function resetPassword(userObj) {
    try {
        const response = await post(RESET_PASSWORD_PATH, userObj);

        if (response.data) {
            return { success: true, message: "重设密码成功", data: response.data };
        } else {
            return { success: false, message: "重设密码失败" };
        }
    } catch (error) {
        // 返回后端错误信息或默认错误
        const message = error?.response?.data?.message || error.message || "重置密码失败";
        return { success: false, message };
    }
}